import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Plus, Edit, Trash } from "lucide-react";
import type { ServiceResponse } from "@/types/services";
import ServiceOptionsList from "./ServiceOptionsList";
import CreateServiceOptionModal from "../modals/CreateServiceOptionModal";
import EditServiceModal from "../modals/EditServiceModal";
import ServiceDetailDrawer from "./ServiceDetailDrawer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ServiceCardProps {
  service: ServiceResponse;
}

export default function ServiceCard({ service }: ServiceCardProps) {
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('DELETE', `/marketplace/services/${service.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      toast({
        title: "Service deleted",
        description: "The service has been successfully deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete the service. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this service?")) {
      deleteMutation.mutate();
    }
  };

  return (
    <Card className="athlyn-card hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowDetailDrawer(true)}>
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <CardTitle className="text-lg">{service.name}</CardTitle>
            <p className="text-sm text-muted-foreground">{service.vendorName}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={service.isActive ? "default" : "secondary"}>
              {service.isActive ? "Active" : "Inactive"}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowEditModal(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDelete}
                  className="text-destructive"
                >
                  <Trash className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium">Options</h4>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setShowCreateOptionModal(true);
            }}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Option
          </Button>
        </div>

        <ServiceOptionsList serviceId={service.id} />
      </CardContent>

      <CreateServiceOptionModal
        open={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        serviceId={service.id}
        serviceName={service.name}
      />

      <EditServiceModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        service={service}
      />

      <ServiceDetailDrawer
        open={showDetailDrawer}
        onClose={() => setShowDetailDrawer(false)}
        serviceId={service.id}
      />
    </Card>
  );
}